'use client';

import { ReactNode } from 'react';
import { Button as UIButton } from '@headlessui/react';
import { clsx } from 'clsx';

/**
 * A string representing the button's variant, which can be 'primary',
 * 'secondary', or 'tertiary'.
 */
type TButtonVariant = 'primary' | 'secondary' | 'tertiary';

interface IButtonViewProps {
  /**
   * The content to be displayed inside the button.
   */
  children: ReactNode;
  /**
   * An optional string representing additional CSS classes to be applied to
   * the button.
   */
  className?: string;
  /**
   * A boolean indicating whether the button should have a dark theme.
   */
  dark?: boolean;
  /**
   * A boolean indicating whether the button should be disabled.x
   */
  disabled?: boolean;
  /**
   * A callback function that will be executed when the button is clicked.
   */
  onClick?: () => void;
  /**
   * Helpful HTML title attribute for the button.
   */
  title?: string;
  /**
   * Button type attribute for the button.
   */
  type?: 'button' | 'submit' | 'reset';
  /**
   * A string representing the button's variant, which can be 'primary',
   * 'secondary', or 'tertiary'.
   */
  variant?: TButtonVariant;
}

/**
 * -----------------------------------------------------------------------------
 * This is the main button element that should be used across the app, providing
 * `'primary' | 'secondary' | 'tertiary'` variant styling options.
 * TODO: @keval-nl - To discuss:: Removed unnecessary classes and complexity.
 * TODO: @keval-nl we need a lot to discuss on this component
 */
export function ButtonAlt2({
  children,
  className,
  dark,
  disabled,
  onClick,
  title,
  type = 'button',
  variant = 'primary',
}: Readonly<IButtonViewProps>) {
  return (
    <UIButton
      className={clsx(
        className,
        variant === 'primary' &&
        `primary-btn ${dark && 'border-gray-950 bg-gray-950 text-white'}`,
        variant === 'secondary' &&
        `secondary-btn ${dark && 'border-black text-black'}`,
        variant === 'tertiary' && `tertiary-btn ${dark && 'text-white'}`,
      )}
      disabled={disabled}
      onClick={onClick}
      title={title}
      type={type}
    >
      {children}
    </UIButton>
  );
}
